import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Pencil, Plus } from "lucide-react";
import toast from "react-hot-toast";

import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Switch } from "@/components/ui/Switch";
import DataTable from "@/components/shared/DataTable";
import useLiveResource from "@/hooks/useLiveResource";
import { CenteredSpinner } from "@/components/shared/Loader";
import ShiftTimingService from "@/services/shiftTimingService";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import { to24HourDisplay } from "@/utils/shiftTime";
import AddEditShiftTimingModal from "./AddEditShiftTimingModal";

/**
 * Predefined shift timing, embedded as a card inside the existing
 * Organization Settings page (index.jsx) — there is deliberately no
 * separate route/module, and no permission codes of its own.
 *
 * `canEdit` is the same TENANT_CONFIG_EDIT-derived flag index.jsx passes to
 * every other card on this page (Data Visibility, Currency, Consent,
 * GuardianSettingsCard) — Shift Timing is just another Organization
 * Settings card, so it reuses that page's existing permission rather than
 * having its own. It's UX only: the backend independently enforces
 * TENANT_CONFIG_EDIT on create/update via require_permission(), so a
 * crafted request without it is rejected regardless of what this component
 * renders. The card is always shown when the page itself is reachable (the
 * page-level gate is TENANT_CONFIG_VIEW OR TENANT_CONFIG_EDIT); `canEdit`
 * only controls whether Add/Edit/enable-disable controls are interactive.
 * The job form's shift picker (predefined dropdown + Custom option) is a
 * separate, unrestricted endpoint — unaffected by this flag either way.
 */
const ShiftTimingSettingsCard = ({ canEdit }) => {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [modal, setModal] = useState(null); // { mode: "add" | "edit", shift? }

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await ShiftTimingService.list();
      setShifts(Array.isArray(result?.data) ? result.data : []);
    } catch (err) {
      console.error("Failed to load shift timings:", err);
      setError(apiErrorMessageFormat(err, "Failed to load predefined shifts."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Two admins can have Organization Settings open at once, and the server
  // already publishes `shift_timing.changed` to the tenant on every add,
  // rename and disable — nothing listened, so neither admin saw the other's
  // work until they reloaded.
  useLiveResource({
    keys: "shift_timing",
    onChange: load,
  });

  const handleToggleActive = async (shift) => {
    try {
      setSavingId(shift.id);
      await ShiftTimingService.update(shift.id, { isActive: !shift.isActive });
      toast.success(shift.isActive ? "Shift disabled." : "Shift enabled.");
      await load();
    } catch (err) {
      toast.error(apiErrorMessageFormat(err, "Failed to update shift."));
    } finally {
      setSavingId(null);
    }
  };

  const columns = useMemo(
    () => [
      { key: "name", label: "Shift Name", sortable: true, className: "font-medium" },
      { key: "startTime", label: "Start Time", sortable: true, render: (value) => to24HourDisplay(value) },
      { key: "endTime", label: "End Time", sortable: true, render: (value) => to24HourDisplay(value) },
      { key: "timezone", label: "Time Zone", sortable: true },
      {
        key: "isActive",
        label: "Status",
        render: (_value, shift) => (
          <Switch
            checked={shift.isActive}
            disabled={!canEdit || savingId !== null}
            readOnly={!canEdit}
            label={`${shift.name} active`}
            onToggle={() => handleToggleActive(shift)}
          />
        ),
      },
      ...(canEdit
        ? [
            {
              key: "actions",
              label: "Actions",
              align: "right",
              render: (_value, shift) => (
                <Button
                  type="button"
                  onClick={() => setModal({ mode: "edit", shift })}
                  className="inline-flex items-center gap-1 rounded-none px-0 py-0 text-xs text-text-secondary hover:text-text-primary"
                >
                  <Pencil size={13} />
                  Edit
                </Button>
              ),
            },
          ]
        : []),
    ],
    // handleToggleActive is re-created every render; the columns only need to
    // be rebuilt when what they render changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canEdit, savingId],
  );

  const closeModal = () => setModal(null);
  const handleSaved = () => {
    toast.success(modal?.mode === "edit" ? "Shift updated." : "Shift created.");
    closeModal();
    load();
  };

  return (
    <Card className="p-6 bg-background-secondary border border-border-primary rounded-xl">
      <div className="flex flex-col gap-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1.5">
            <h2 className="text-lg font-semibold text-text-primary">Shift Timing</h2>
            <p className="text-[13px] text-text-secondary leading-relaxed">
              Define reusable shift timings that jobs can select instead of entering shift times manually. Disabling a shift hides it from new job
              selections without affecting jobs that already use it.
            </p>
          </div>
          {canEdit && (
            <Button
              type="button"
              onClick={() => setModal({ mode: "add" })}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm bg-button-secondary text-text-base"
            >
              <Plus size={14} />
              Add Shift
            </Button>
          )}
        </div>

        {loading ? (
          <CenteredSpinner />
        ) : error ? (
          <p className="text-[13px] text-tone-rose">{error}</p>
        ) : shifts.length === 0 ? (
          // Rendered here rather than left to DataTable, which always prints
          // its own "No data available".
          <p className="text-[13px] text-text-secondary">No predefined shifts configured yet.</p>
        ) : (
          // A tenant configures a handful of shifts, so the whole list is
          // held locally and paging it would only get in the way — hence
          // showPagination={false} with pageSize covering every row, since
          // DataTable always slices by pageSize.
          <DataTable columns={columns} apiData={shifts} rowKey="id" defaultSort={{ key: "name", order: "asc" }} showPagination={false} pageSize={shifts.length} />
        )}
      </div>

      {modal && <AddEditShiftTimingModal isOpen={!!modal} mode={modal.mode} shift={modal.shift} onClose={closeModal} onSaved={handleSaved} />}
    </Card>
  );
};

export default ShiftTimingSettingsCard;
